import React, { useEffect, useState } from 'react';
import InvoiceLink from './InvoiceLink';
import { Scissors, MapPin, Star, Wallet, Smartphone } from 'lucide-react';
import apiService from '../apiService';
import mmPay from './mmPay';
import { ratingText } from './ratingText';

/**
 * Instituts de beauté web — salons, détail (services multi-sélection),
 * créneaux du jour (availability serveur), réservation (sur place / solde /
 * Mobile Money pay-first), mes rendez-vous + annulation.
 */
const d2s = (d) => d.toISOString().split('T')[0];

export function BeautySalons({ nav, citySlug }) {
  const [salons, setSalons] = useState(null);
  useEffect(() => {
    const qs = new URLSearchParams({ limit: 40 });
    if (citySlug) qs.set('city_slug', citySlug);
    apiService.get(`/beauty/salons?${qs}`).then(r => setSalons(r?.salons || r?.suppliers || [])).catch(() => setSalons([]));
  }, [citySlug]);

  if (salons === null) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;
  return (
    <div className="mk-container">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
        <button className="mk-btn ghost" onClick={() => nav({ page: 'beauty-bookings' })}><Scissors size={16} /> Mes rendez-vous</button>
      </div>
      {!salons.length && <div className="mk-center"><Scissors size={42} /> Aucun institut disponible pour le moment.</div>}
      <div className="mk-section" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 16 }}>
        {salons.map(s => (
          <button key={s.supplier_id} className="mk-card" onClick={() => nav({ page: 'beauty-salon', supplierId: s.supplier_id })}>
            <div className="mk-card-imgwrap">
              {(s.cover || s.logo) ? <img className="mk-card-img" style={{ height: 140 }} src={s.cover || s.logo} alt="" loading="lazy" /> : <div className="mk-card-img mk-card-ph" style={{ height: 140 }}>💇</div>}
            </div>
            <span className="mk-card-title">{s.business_name}</span>
            <span className="mk-meta"><Star size={11} fill="#FFB900" color="#FFB900" /> {ratingText(s.rating, s.rating_count)}{s.address ? <> · <MapPin size={11} /> {s.address}</> : null}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function BeautySalonDetail({ supplierId, nav, currentUser }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]); // service_ids
  const [date, setDate] = useState(d2s(new Date()));
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState(null);
  const [atHome, setAtHome] = useState(false);
  const [homeAddress, setHomeAddress] = useState('');
  const [pay, setPay] = useState('on_site');
  const [phone, setPhone] = useState('');
  const [telecom, setTelecom] = useState('AM');
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiService.get(`/beauty/salons/${supplierId}`).then(r => { if (r?.success) setData(r); }).catch(() => {});
  }, [supplierId]);

  const services = data?.services || [];
  const salon = data?.salon || data?.supplier || {};
  const config = data?.config || {};
  const chosen = services.filter(s => selected.includes(s.service_id));
  const total = chosen.reduce((s, x) => s + (x.price || 0), 0);
  const homeAllowed = config.at_home_enabled !== false && chosen.every(s => s.at_home !== false);

  // Créneaux dès que services + date choisis
  useEffect(() => {
    if (!selected.length) { setSlots(null); setSlot(null); return; }
    setSlots(null); setSlot(null);
    apiService.get(`/beauty/salons/${supplierId}/availability?date=${date}&services=${selected.join(',')}`)
      .then(r => setSlots(r?.slots || []))
      .catch(() => setSlots([]));
  }, [selected, date, supplierId]);

  if (!data) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;

  const book = async () => {
    setError(null);
    if (!slot) return setError('Choisissez un créneau.');
    if (atHome && !homeAddress.trim()) return setError('Indiquez votre adresse pour la prestation à domicile.');
    setBusy(true);
    try {
      const payload = {
        supplier_id: supplierId, service_ids: selected, date, start_time: slot,
        at_home: atHome, home_address: atHome ? homeAddress.trim() : '',
        payment_method: pay,
      };
      if (pay === 'mobile_money') {
        const { paymentId } = await mmPay({
          userId: currentUser.user_id, amount: total, phone, telecom,
          label: `RDV ${salon.business_name}`, planType: 'beauty_booking', onPhase: setPhase,
        });
        payload.serdipay_payment_id = paymentId;
      }
      const res = await apiService.post('/beauty/bookings', payload);
      if (!res?.success) throw new Error(res?.error || 'Réservation impossible');
      nav({ page: 'beauty-bookings' });
    } catch (e) { setError(e?.error || e?.message || 'Erreur'); }
    finally { setBusy(false); setPhase(null); }
  };

  return (
    <div className="mk-container">
      <div className="mk-two">
        <div className="mk-panel">
          <h3>{salon.business_name || 'Institut'}</h3>
          {salon.address && <p className="mk-note"><MapPin size={13} /> {salon.address}</p>}
          <h4 style={{ margin: '12px 0 6px' }}>Services</h4>
          {services.map(s => {
            const on = selected.includes(s.service_id);
            return (
              <button key={s.service_id} className={`mk-opt${on ? ' on' : ''}`}
                onClick={() => setSelected(on ? selected.filter(x => x !== s.service_id) : [...selected, s.service_id])}>
                <span className="box">{on ? '✓' : ''}</span>
                <span className="name">{s.name}{s.duration_min ? ` · ${s.duration_min} min` : ''}</span>
                <span className="delta">{(s.price || 0).toLocaleString('fr-FR')} FC</span>
              </button>
            );
          })}
          {!services.length && <p className="mk-note">Aucun service publié.</p>}
        </div>

        <div className="mk-panel">
          <h3>Rendez-vous</h3>
          <div className="mk-field"><label>Date</label>
            <input type="date" value={date} min={d2s(new Date())} onChange={e => setDate(e.target.value)} /></div>
          {selected.length > 0 && (
            <>
              <label style={{ fontSize: '.82rem', fontWeight: 700, color: 'var(--mb-text-2)' }}>Créneaux disponibles</label>
              {slots === null ? <div className="mk-center" style={{ padding: 20 }}><div className="mk-spin" /></div> : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '8px 0 4px' }}>
                  {slots.map(s => (
                    <button key={s} className="mk-raytab" style={slot === s ? { background: 'var(--mb-charcoal)', color: '#fff', borderColor: 'var(--mb-charcoal)' } : {}} onClick={() => setSlot(s)}>{s}</button>
                  ))}
                  {!slots.length && <p className="mk-note warn">Aucun créneau ce jour — essayez une autre date.</p>}
                </div>
              )}
            </>
          )}
          {homeAllowed && (
            <button className={`mk-opt${atHome ? ' on' : ''}`} onClick={() => setAtHome(!atHome)} style={{ marginTop: 6 }}>
              <span className="box">{atHome ? '✓' : ''}</span>
              <span className="name">Prestation à domicile</span>
            </button>
          )}
          {atHome && (
            <div className="mk-field"><label>Votre adresse</label>
              <input value={homeAddress} onChange={e => setHomeAddress(e.target.value)} placeholder="Adresse complète" /></div>
          )}
          <div className="mk-pay" style={{ marginTop: 8 }}>
            <button className={pay === 'on_site' ? 'on' : ''} onClick={() => setPay('on_site')}>💵 Sur place</button>
            <button className={pay === 'balance' ? 'on' : ''} onClick={() => setPay('balance')}><Wallet size={18} />Solde</button>
            <button className={pay === 'mobile_money' ? 'on' : ''} onClick={() => setPay('mobile_money')}><Smartphone size={18} />Mobile Money</button>
          </div>
          {pay === 'mobile_money' && (
            <div style={{ marginTop: 8 }}>
              <div className="mk-field"><label>Numéro Mobile Money</label>
                <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="0991234567" inputMode="tel" /></div>
              <div className="mk-field"><label>Opérateur</label>
                <select value={telecom} onChange={e => setTelecom(e.target.value)}>
                  <option value="AM">Airtel Money</option><option value="MP">M-Pesa</option><option value="OM">Orange Money</option>
                </select></div>
            </div>
          )}
          <div className="mk-total grand"><span>Total ({chosen.length} service{chosen.length > 1 ? 's' : ''})</span><span>{total.toLocaleString('fr-FR')} FC</span></div>
          {error && <p className="mk-note err" style={{ marginTop: 8 }}>{error}</p>}
          <button className="mk-btn primary block" style={{ marginTop: 12 }} disabled={busy || !selected.length} onClick={book}>
            {busy ? (phase || 'Réservation…') : 'Réserver le rendez-vous'}
          </button>
        </div>
      </div>
    </div>
  );
}

const BB = {
  pending: ['En attente', '#FFB900'], confirmed: ['Confirmé', '#10B981'], completed: ['Terminé', '#5C636E'],
  cancelled: ['Annulé', '#DC2626'], no_show: ['Non présenté', '#DC2626'],
};

export function BeautyBookings({ nav }) {
  const [list, setList] = useState(null);
  const load = () => apiService.get('/beauty/bookings?role=client').then(r => setList(r?.bookings || [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    if (!window.confirm('Annuler ce rendez-vous ?')) return;
    const r = await apiService.patch(`/beauty/bookings/${id}/cancel`, {}).catch(e => e);
    if (!r?.success) alert(r?.error || r?.message || 'Annulation impossible');
    load();
  };

  if (list === null) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;
  return (
    <div className="mk-container">
      <div className="mk-panel" style={{ marginTop: 16 }}>
        <h3>💇 Mes rendez-vous beauté</h3>
        {!list.length && <div className="mk-center">Aucun rendez-vous. <button className="mk-btn primary" onClick={() => nav({ page: 'beauty' })}>Voir les instituts</button></div>}
        {list.map(b => {
          const [lbl, color] = BB[b.status] || [b.status, '#5C636E'];
          return (
            <div className="mk-line" key={b.booking_id}>
              <div className="info">
                <div className="name">{b.salon_name || b.supplier?.business_name || 'Institut'}</div>
                <div className="opt">{b.date} à {b.start_time} · {(b.total_amount || b.total || 0).toLocaleString('fr-FR')} FC{b.at_home ? ' · À domicile' : ''}</div>
                <div className="opt">{(b.services || []).map(s => s.name).join(', ')}</div>
              </div>
              <span className="mk-open" style={{ background: `${color}1c`, color }}>{lbl}</span>
              <InvoiceLink type="beauty" id={b.booking_id} compact />
              {['pending', 'confirmed'].includes(b.status) && (
                <button className="mk-iconbtn" title="Annuler" onClick={() => cancel(b.booking_id)} style={{ color: 'var(--mb-danger)' }}>✕</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
